import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { format } from "date-fns";
import { ArrowRightLeft, Plus, Eye, Container, Pencil, Printer, Filter, Phone, MessageCircle, Trash2, MoreHorizontal } from "lucide-react";
import { printMultipleLR, printSingleLR } from "../../utils/printLR";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { CreateTransferDialog } from "@/components/CreateTransferDialog";
import { useAuth } from "@/context/AuthContext";

import ConsignmentMovement from "@/components/ConsignmentMovement";
import { getApiErrorMessage } from "@/utils/apiError";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_STYLE = {
  "Active":     "bg-blue-50 text-blue-700 border-blue-200",
  // New statuses
  "Open":       "bg-blue-50 text-blue-700 border-blue-200",
  "Pick-Up Done": "bg-blue-50 text-blue-700 border-blue-200",
  "In Transit": "bg-amber-50 text-amber-700 border-amber-200",
  "At Warehouse":"bg-purple-50 text-purple-700 border-purple-200",
  "Out for Delivery": "bg-cyan-50 text-cyan-700 border-cyan-200",
  "Awaiting Completion": "bg-orange-50 text-orange-700 border-orange-200",
  "Delivery Complete": "bg-green-50 text-green-700 border-green-200",
  "Complete":   "bg-green-50 text-green-700 border-green-200",
  "Delivered":  "bg-green-50 text-green-700 border-green-200",
  "On Hold":    "bg-red-50 text-red-600 border-red-200",
  // Legacy (keep for old records)
  Scheduled:    "bg-violet-50 text-violet-700 border-violet-200",
  Completed:    "bg-green-50 text-green-700 border-green-200",
  Cancelled:    "bg-red-50 text-red-700 border-red-200",
};

const COMPLETED_STATUSES = new Set(["Delivery Complete", "Complete", "Completed", "Delivered"]);
const EMPTY_FILTERS = { search: "", rm: "all", warehouseLocation: "all", status: "all" };
const CONSIGNMENT_STATUSES = ["Pick-Up Done", "Out for Delivery", "In Transit", "At Warehouse", "Completed"];
const visibleStatus = status => ({
  "Awaiting Pickup": "Pick-Up Done",
  "Awaiting Completion": "Out for Delivery",
  "Delivery Complete": "Completed",
  "Complete": "Completed",
  "Delivered": "Completed",
}[status] || status);

const phoneDigits = value => String(value || "").replace(/\D/g, "");
const whatsappNumber = value => {
  const digits = phoneDigits(value);
  return digits.length === 10 ? `91${digits}` : digits;
};

const ageInDays = date => {
  if (!date) return "—";
  const pickup = new Date(`${date}T00:00:00`);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.max(0, Math.floor((today - pickup) / 86400000));
};

const TYPE_BADGE = {
  from_location: "bg-slate-100 text-slate-600",
  warehouse:     "bg-purple-100 text-purple-700",
  to_location:   "bg-teal-100 text-teal-700",
};

const TYPE_LABEL = {
  from_location: "Pickup",
  warehouse:     "Warehouse",
  to_location:   "Delivery",
};

/* ── Edit Consignment dialog ──────────────────────────────────────────────── */
function EditTransferDialog({ transfer, open, onClose, onSaved }) {
  const [form, setForm] = useState({
    transfer_date: "",
    origin_type: "", origin_label: "",
    destination_type: "", destination_label: "",
    status: "Pick-Up Done", current_location: "",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && transfer) {
      setForm({
        transfer_date:    transfer.transfer_date || "",
        origin_type:      transfer.origin_type || "from_location",
        origin_label:     transfer.origin_label || "",
        destination_type: transfer.destination_type || "to_location",
        destination_label:transfer.destination_label || "",
        status:            transfer.status || "Active",
        current_location:  transfer.current_location || "",
        reason: "",
      });
    }
  }, [open, transfer]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const { data } = await axios.patch(
        `${API}/consignments/${transfer.id}`,
        form,
        { withCredentials: true }
      );
      toast.success("Consignment updated.");
      onSaved(data);
      onClose();
    } catch (err) {
      toast.error(getApiErrorMessage(err, "Failed to update consignment."));
    } finally { setSaving(false); }
  };

  const CLS = "rounded-none border-[#E2E8F0] focus-visible:ring-0 focus-visible:border-[#1B2A6B] h-11 text-sm";
  const SEL_CLS = "rounded-none border-[#E2E8F0] focus:ring-0 focus:border-[#1B2A6B] h-11 text-sm";
  const lbl = "text-[11px] font-bold tracking-widest uppercase text-[#1B2A6B] block mb-1.5";
  const ORIGIN_OPTS = [
    { value: "from_location", label: "Pickup" },
    { value: "warehouse",     label: "Warehouse" },
    { value: "to_location",   label: "Delivery Point" },
  ];

  return (
    <Dialog open={open} onOpenChange={o => !o && onClose()}>
      <DialogContent className="max-w-md rounded-none p-0 gap-0">
        <div className="bg-[#1B2A6B] px-6 py-4 border-b-4 border-[#FFD700]">
          <DialogTitle className="text-white font-bold tracking-wide text-base">Edit Consignment</DialogTitle>
          <p className="text-white/60 text-xs mt-0.5 font-mono">{transfer?.lr_number}</p>
        </div>
        <div className="px-6 py-5 space-y-4">
          <div>
            <label className={lbl}>Consignment Date</label>
            <input type="date" value={form.transfer_date}
              onChange={e => setForm(f => ({ ...f, transfer_date: e.target.value }))}
              className="w-full rounded-none border border-[#E2E8F0] focus:border-[#1B2A6B] focus:outline-none px-3 h-11 text-sm" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={lbl}>Origin Type</label>
              <Select value={form.origin_type} onValueChange={v => setForm(f => ({ ...f, origin_type: v }))}>
                <SelectTrigger className={SEL_CLS}><SelectValue /></SelectTrigger>
                <SelectContent className="rounded-none">
                  {ORIGIN_OPTS.map(o => <SelectItem key={o.value} value={o.value} className="rounded-none">{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className={lbl}>Destination Type</label>
              <Select value={form.destination_type} onValueChange={v => setForm(f => ({ ...f, destination_type: v }))}>
                <SelectTrigger className={SEL_CLS}><SelectValue /></SelectTrigger>
                <SelectContent className="rounded-none">
                  {ORIGIN_OPTS.map(o => <SelectItem key={o.value} value={o.value} className="rounded-none">{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <label className={lbl}>Origin Label</label>
            <Input value={form.origin_label}
              onChange={e => setForm(f => ({ ...f, origin_label: e.target.value }))}
              placeholder="Origin address or name" className={CLS} />
          </div>
          <div>
            <label className={lbl}>Destination Label</label>
            <Input value={form.destination_label}
              onChange={e => setForm(f => ({ ...f, destination_label: e.target.value }))}
              placeholder="Destination address or name" className={CLS} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={lbl}>Consignment Status</label>
              <Select value={form.status} onValueChange={v => setForm(f => ({ ...f, status: v }))}>
                <SelectTrigger className={SEL_CLS}><SelectValue /></SelectTrigger>
                <SelectContent className="rounded-none">
                  {CONSIGNMENT_STATUSES.map(status =>
                    <SelectItem key={status} value={status} className="rounded-none">{status}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className={lbl}>Reason for status correction</label>
              <Input value={form.reason || ""}
                onChange={e => setForm(f => ({ ...f, reason: e.target.value }))}
                placeholder="Required when changing status" className={CLS} />
            </div>
          </div>
        </div>
        <DialogFooter className="px-6 py-4 bg-[#F9FAFB] border-t border-[#E2E8F0] gap-2">
          <button type="button" onClick={onClose}
            className="px-5 py-2 text-xs font-bold uppercase tracking-widest text-[#475569] border border-[#E2E8F0] hover:border-[#CBD5E1] transition-colors">
            Cancel
          </button>
          <Button onClick={handleSave} disabled={saving}
            className="rounded-none bg-[#1B2A6B] hover:bg-[#152257] text-white font-bold tracking-widest uppercase text-xs h-9 px-6 border-b-2 border-[#FFD700] disabled:opacity-60">
            {saving ? "Saving..." : "Save Changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ── Assign-to-Consignment dialog ─────────────────────────────────────── */
function AssignConsignmentDialog({ transfer, open, onClose, onSaved }) {
  return <Dialog open={open} onOpenChange={o => !o && onClose()}>
    <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
      <DialogTitle>Consignment Movement</DialogTitle>
      {transfer && <ConsignmentMovement transfer={transfer} onSaved={onSaved} />}
    </DialogContent>
  </Dialog>;
}

/* ── Main page ─────────────────────────────────────────────────────────── */
export default function TransfersPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isRM = user?.role === "relationship_manager";
  const isSuperAdmin = user?.role === "superadmin";
  const [transfers,    setTransfers]    = useState([]);
  const [consignments, setConsignments] = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [showCreate,   setShowCreate]   = useState(false);
  const [assignTarget, setAssignTarget] = useState(null); // transfer to assign
  const [editTransfer, setEditTransfer] = useState(null); // transfer to edit
  const [pincodeEdit, setPincodeEdit] = useState(null);
  const [pincodeSaving, setPincodeSaving] = useState(null);
  const [generatingLR, setGeneratingLR] = useState(null);
  const [showMultipleLR, setShowMultipleLR] = useState(false);
  const [selectedLRIds, setSelectedLRIds] = useState([]);
  const [multipleLRCopyMode, setMultipleLRCopyMode] = useState("both");
  const [generatingMultipleLR, setGeneratingMultipleLR] = useState(false);
  const [filterDraft, setFilterDraft] = useState(EMPTY_FILTERS);
  const [activeFilters, setActiveFilters] = useState(EMPTY_FILTERS);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [filterOptions, setFilterOptions] = useState({ rms: [], drop_areas: [], current_locations: [], statuses: [] });
  const loadingRef = useRef(false);
  const loadMoreRef = useRef(null);
  const activeTab = useLocation().pathname.endsWith("/completed") ? "completed" : "active";

  const fetchTransfers = async (requestedPage = 1, append = false, filters = activeFilters) => {
    if (append && loadingRef.current) return;
    if (append) { loadingRef.current = true; setLoadingMore(true); }
    try {
      const { data } = await axios.get(`${API}/consignments/paginated`, {
        params: {
          page: requestedPage, limit: 50, completed: activeTab === "completed",
          rm: filters.rm, warehouse_location: filters.warehouseLocation, status: filters.status,
          search: filters.search,
        },
        withCredentials: true,
      });
      setTransfers(previous => append ? [...previous, ...data.items] : data.items);
      setPage(data.page); setTotal(data.total); setHasMore(data.has_more);
      if (data.filter_options) setFilterOptions(data.filter_options);
    } catch { toast.error("Failed to load consignments."); }
    finally { setLoading(false); if (append) { loadingRef.current = false; setLoadingMore(false); } }
  };

  useEffect(() => {
    setFilterDraft(EMPTY_FILTERS);
    setActiveFilters(EMPTY_FILTERS);
    setLoading(true);
    fetchTransfers(1, false, EMPTY_FILTERS);
  }, [activeTab]);

  useEffect(() => {
    axios.get(`${API}/trips`, { withCredentials: true })
      .then(response => setConsignments(response.data))
      .catch(() => toast.error("Failed to load trip sheets."));
  }, []);

  useEffect(() => {
    const target = loadMoreRef.current;
    if (!target || !hasMore) return undefined;
    const observer = new IntersectionObserver(entries => {
      if (entries[0]?.isIntersecting && !loadingRef.current) fetchTransfers(page + 1, true, activeFilters);
    }, { rootMargin: "250px" });
    observer.observe(target);
    return () => observer.disconnect();
  }, [hasMore, page, activeFilters, activeTab]);

  const handleAssigned = (updated) => {
    const belongsHere = COMPLETED_STATUSES.has(updated.status) === (activeTab === "completed");
    setTransfers(previous => belongsHere
      ? previous.map(transfer => transfer.id === updated.id ? updated : transfer)
      : previous.filter(transfer => transfer.id !== updated.id));
    if (!belongsHere) setTotal(value => Math.max(0, value - 1));
  };

  const handleEdited = (updated) =>
    setTransfers(prev => prev.map(t => t.id === updated.id ? updated : t));

  const saveDropPincode = async (transfer, value) => {
    const dropPincode = value.trim();
    setPincodeEdit(null);
    if (dropPincode === (transfer.drop_pincode || "")) return;
    if (dropPincode && !/^\d{6}$/.test(dropPincode)) {
      toast.error("Drop Pincode must contain exactly 6 digits.");
      return;
    }
    setPincodeSaving(transfer.id);
    try {
      const { data } = await axios.patch(
        `${API}/consignments/${transfer.id}`,
        { drop_pincode: dropPincode },
        { withCredentials: true }
      );
      handleEdited(data);
      toast.success("Drop Pincode updated.");
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Failed to update Drop Pincode."));
    } finally {
      setPincodeSaving(null);
    }
  };

  const generateLR = async transfer => {
    const printWindow = window.open("", "_blank", "width=900,height=700,toolbar=no,menubar=no");
    if (!printWindow) {
      toast.error("Pop-up blocked. Please allow pop-ups to generate the LR.");
      return;
    }
    printWindow.document.write("<p style='font-family:Arial;padding:24px'>Generating latest LR...</p>");
    setGeneratingLR(transfer.id);
    try {
      const { data } = await axios.post(`${API}/consignments/${transfer.id}/lr-versions`, {}, { withCredentials: true });
      printSingleLR(data.snapshot, printWindow);
    } catch (error) {
      printWindow.close();
      toast.error(getApiErrorMessage(error, "Failed to generate LR."));
    } finally {
      setGeneratingLR(null);
    }
  };

  const generateMultipleLR = async () => {
    if (!selectedLRIds.length) {
      toast.error("Select at least one consignment.");
      return;
    }
    const printWindow = window.open("", "_blank", "width=900,height=700,toolbar=no,menubar=no");
    if (!printWindow) {
      toast.error("Pop-up blocked. Please allow pop-ups to generate the LRs.");
      return;
    }
    printWindow.document.write("<p style='font-family:Arial;padding:24px'>Generating selected LRs...</p>");
    setGeneratingMultipleLR(true);
    try {
      const snapshots = await Promise.all(selectedLRIds.map(async id => {
        const { data } = await axios.post(`${API}/consignments/${id}/lr-versions`, {}, { withCredentials: true });
        return data.snapshot;
      }));
      printMultipleLR(snapshots, printWindow, multipleLRCopyMode);
      setShowMultipleLR(false);
      setSelectedLRIds([]);
      setMultipleLRCopyMode("both");
    } catch (error) {
      printWindow.close();
      toast.error(getApiErrorMessage(error, "Failed to generate the selected LRs."));
    } finally {
      setGeneratingMultipleLR(false);
    }
  };

  const deleteConsignment = async transfer => {
    if (!window.confirm(`Are you sure you want to delete consignment ${transfer.lr_number || transfer.customer_name || ""}? This action cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/consignments/${transfer.id}`, { withCredentials: true });
      setTransfers(previous => previous.filter(item => item.id !== transfer.id));
      setSelectedLRIds(previous => previous.filter(id => id !== transfer.id));
      setTotal(value => Math.max(0, value - 1));
      toast.success("Consignment deleted.");
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Failed to delete consignment."));
    }
  };

  const tabTransfers = transfers;
  const rmOptions = filterOptions.rms;
  const warehouseLocationOptions = filterOptions.warehouse_locations || [];
  const statusOptions = CONSIGNMENT_STATUSES;
  const visibleTransfers = tabTransfers;

  return (
    <div className="space-y-5" data-testid="transfers-page">

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-[#1B2A6B]">{activeTab === "active" ? "Active" : "Completed"} Consignments</h2>
          <p className="text-xs text-[#475569] mt-0.5">{total} consignment{total !== 1 ? "s" : ""} · {visibleTransfers.length} loaded</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            type="button"
            onClick={() => setShowMultipleLR(true)}
            disabled={!visibleTransfers.length}
            className="rounded-none bg-white hover:bg-[#EEF0FA] text-[#1B2A6B] font-bold tracking-widest uppercase text-xs h-10 px-5 border border-[#1B2A6B]"
          >
            <Printer size={14} className="mr-1.5" />Print Multiple LR
          </Button>
          {!isRM && <Button
            data-testid="new-transfer-btn"
            onClick={() => setShowCreate(true)}
            className="rounded-none bg-[#1B2A6B] hover:bg-[#152257] text-white font-bold tracking-widest uppercase text-xs h-10 px-5 border-b-2 border-[#FFD700]"
          >
            <Plus size={14} className="mr-1.5" />New Consignment
          </Button>}
        </div>
      </div>

      <div className="bg-white border border-[#E2E8F0] p-4" data-testid="consignment-filters">
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-[1.4fr_1fr_1fr_1fr_auto] gap-3 items-end">
          <div>
            <label className="text-[11px] font-bold tracking-widest uppercase text-[#1B2A6B] block mb-1.5">Customer Name or Number</label>
            <input type="search" value={filterDraft.search} onChange={event => setFilterDraft(value => ({ ...value, search: event.target.value }))} placeholder="Search customer" className="w-full h-10 border border-[#E2E8F0] bg-white px-3 text-sm focus:border-[#1B2A6B] focus:outline-none" />
          </div>
          <div>
            <label className="text-[11px] font-bold tracking-widest uppercase text-[#1B2A6B] block mb-1.5">RM</label>
            <select value={filterDraft.rm} onChange={event => setFilterDraft(value => ({ ...value, rm: event.target.value }))} className="w-full h-10 border border-[#E2E8F0] bg-white px-3 text-sm focus:border-[#1B2A6B] focus:outline-none">
              <option value="all">All</option>
              {rmOptions.map(rm => <option key={rm} value={rm}>{rm}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[11px] font-bold tracking-widest uppercase text-[#1B2A6B] block mb-1.5">Warehouse Location</label>
            <select value={filterDraft.warehouseLocation} onChange={event => setFilterDraft(value => ({ ...value, warehouseLocation: event.target.value }))} className="w-full h-10 border border-[#E2E8F0] bg-white px-3 text-sm focus:border-[#1B2A6B] focus:outline-none">
              <option value="all">All</option>
              {warehouseLocationOptions.map(location => <option key={location} value={location}>{location}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[11px] font-bold tracking-widest uppercase text-[#1B2A6B] block mb-1.5">Consignment Status</label>
            <select value={filterDraft.status} onChange={event => setFilterDraft(value => ({ ...value, status: event.target.value }))} className="w-full h-10 border border-[#E2E8F0] bg-white px-3 text-sm focus:border-[#1B2A6B] focus:outline-none">
              <option value="all">All</option>
              {statusOptions.map(status => <option key={status} value={status}>{status}</option>)}
            </select>
          </div>
          <Button type="button" onClick={() => { const filters = { ...filterDraft }; setActiveFilters(filters); setLoading(true); fetchTransfers(1, false, filters); }} className="h-10 rounded-none bg-[#1B2A6B] hover:bg-[#152257] text-white font-bold uppercase tracking-widest text-xs px-5 border-b-2 border-[#FFD700]">
            <Filter size={13} className="mr-1.5" />Filter
          </Button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white border border-[#E2E8F0]">
        {loading ? (
          <div className="flex items-center justify-center py-20 text-sm text-[#94A3B8]">Loading...</div>
        ) : visibleTransfers.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 gap-3">
            <ArrowRightLeft size={40} className="text-[#E2E8F0]" />
            <p className="text-sm text-[#94A3B8]">No {activeTab} consignments.</p>
          </div>
        ) : (
          <div className="overflow-x-auto"><table className="w-full min-w-[1650px] text-sm" data-testid="transfers-table">
            <thead>
              <tr className="bg-[#F9FAFB] border-b border-[#E2E8F0]">
                {["LR Number", "Customer", "RM Name", "Consignment Status", "Godown / Warehouse Location", "Pick-Up Date", "Age", "Pick-Up Zone", "Drop Zone", "Drop Pincode", "More"].map(h => (
                  <th key={h} className="text-left px-4 py-3 text-[10px] font-bold uppercase tracking-widest text-[#475569] whitespace-nowrap">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {visibleTransfers.map((t, idx) => {
                const rowBg = idx % 2 === 0 ? "bg-white" : "bg-[#F8FAFC]";
                return (
                    <tr key={t.id} data-testid={`transfer-row-${idx}`} className={`border-b border-[#E2E8F0] ${rowBg}`}>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className="font-mono font-black text-sm text-[#1B2A6B] bg-[#EEF0FA] px-2 py-0.5 border border-[#C7D2FE] tracking-widest">
                          {t.lr_number || "—"}
                        </span>
                        <div className="mt-2 flex items-center gap-1.5">
                          <button
                            type="button"
                            onClick={() => generateLR(t)}
                            disabled={generatingLR === t.id}
                            data-testid={`generate-lr-${idx}`}
                            aria-label={`Generate LR for ${t.lr_number || "consignment"}`}
                            title="Generate LR"
                            className="inline-flex h-7 w-7 items-center justify-center border border-[#C7D2FE] bg-[#EEF0FA] text-[#1B2A6B] transition-colors hover:border-[#1B2A6B] hover:bg-[#1B2A6B] hover:text-white disabled:opacity-50"
                          >
                            <Printer size={13} className={generatingLR === t.id ? "animate-pulse" : ""} />
                          </button>
                          <button
                            type="button"
                            onClick={() => navigate(`/consignments/${t.id}`)}
                            aria-label={`View ${t.lr_number || "consignment"}`}
                            title="View consignment"
                            className="inline-flex h-7 w-7 items-center justify-center border border-[#1B2A6B] text-[#1B2A6B] transition-colors hover:bg-[#1B2A6B] hover:text-white"
                          >
                            <Eye size={13} />
                          </button>
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <p className="font-semibold text-[#1B2A6B]">{t.customer_name || t.consignor_name || "—"}</p>
                        <div className="mt-1 flex items-center gap-1.5">
                          <span className="text-xs font-bold text-[#334155]">{t.customer_phone || t.consignor_phone || t.consignee_phone || "—"}</span>
                          {phoneDigits(t.customer_phone || t.consignor_phone || t.consignee_phone) && <>
                            <a href={`tel:${phoneDigits(t.customer_phone || t.consignor_phone || t.consignee_phone)}`} aria-label={`Call ${t.customer_name || t.consignor_name || "customer"}`} title="Call" className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-blue-50 text-blue-700 hover:bg-blue-700 hover:text-white"><Phone size={12} /></a>
                            <a href={`https://wa.me/${whatsappNumber(t.customer_phone || t.consignor_phone || t.consignee_phone)}`} target="_blank" rel="noreferrer" aria-label={`WhatsApp ${t.customer_name || t.consignor_name || "customer"}`} title="WhatsApp" className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-green-50 text-green-700 hover:bg-green-700 hover:text-white"><MessageCircle size={12} /></a>
                          </>}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-[#475569]">{t.assigned_to_name || t.booking_incharge || "—"}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider border rounded-sm ${STATUS_STYLE[visibleStatus(t.status)] || STATUS_STYLE.Scheduled}`}>
                          {visibleStatus(t.status)}
                        </span>
                      </td>
                      <td className="px-4 py-3 font-medium text-[#1B2A6B]">{t.warehouse_location || "NA"}</td>
                      <td className="px-4 py-3 whitespace-nowrap">{t.transfer_date ? format(new Date(`${t.transfer_date}T00:00:00`), "dd MMM yyyy") : "—"}</td>
                      <td className="px-4 py-3 font-semibold">{ageInDays(t.transfer_date)}{t.transfer_date ? " days" : ""}</td>
                      <td className="px-4 py-3">{t.pickup_zone || "—"}</td>
                      <td className="px-4 py-3">{t.drop_zone || t.drop_area || "—"}</td>
                      <td
                        className="px-4 py-3 font-mono"
                        onDoubleClick={() => !isRM && setPincodeEdit({ id: t.id, value: t.drop_pincode || "" })}
                        title={!isRM ? "Double-click to update Drop Pincode" : undefined}
                      >
                        {pincodeEdit?.id === t.id ? (
                          <input
                            autoFocus
                            type="text"
                            inputMode="numeric"
                            maxLength={6}
                            value={pincodeEdit.value}
                            onChange={event => setPincodeEdit({ id: t.id, value: event.target.value.replace(/\D/g, "") })}
                            onBlur={() => saveDropPincode(t, pincodeEdit.value)}
                            onKeyDown={event => {
                              if (event.key === "Enter") saveDropPincode(t, pincodeEdit.value);
                              if (event.key === "Escape") setPincodeEdit(null);
                            }}
                            onClick={event => event.stopPropagation()}
                            className="w-24 h-8 border border-[#1B2A6B] bg-white px-2 text-sm font-mono focus:outline-none"
                          />
                        ) : (
                          <span className={!isRM ? "cursor-text hover:bg-[#EEF0FA] px-1 py-0.5" : ""}>
                            {pincodeSaving === t.id ? "Saving..." : t.drop_pincode || "—"}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {!isRM || isSuperAdmin ? <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <button
                              type="button"
                              aria-label={`More actions for ${t.lr_number || "consignment"}`}
                              className="inline-flex h-8 items-center gap-1.5 border border-[#E2E8F0] bg-white px-2.5 text-[10px] font-bold uppercase tracking-wider text-[#475569] transition-colors hover:border-[#1B2A6B] hover:text-[#1B2A6B]"
                            >
                              <MoreHorizontal size={14} />More
                            </button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="min-w-48 rounded-none">
                            {!isRM && <DropdownMenuItem onSelect={() => setAssignTarget(t)} className="gap-2 rounded-none cursor-pointer">
                              <Container size={13} />Manage Movement
                            </DropdownMenuItem>}
                            {!isRM && <DropdownMenuItem onSelect={() => setEditTransfer(t)} className="gap-2 rounded-none cursor-pointer">
                              <Pencil size={13} />Edit
                            </DropdownMenuItem>}
                            {isSuperAdmin && <DropdownMenuItem
                              onSelect={() => deleteConsignment(t)}
                              className="gap-2 rounded-none cursor-pointer text-red-600 focus:text-red-700"
                              data-testid={`delete-consignment-${idx}`}
                            >
                              <Trash2 size={13} />Delete
                            </DropdownMenuItem>}
                          </DropdownMenuContent>
                        </DropdownMenu> : <span className="text-[#CBD5E1]">—</span>}
                      </td>
                    </tr>
                );
              })}
            </tbody>
          </table></div>
        )}
      </div>

      <div ref={loadMoreRef} className="flex min-h-10 items-center justify-center text-xs text-[#64748B]">
        {loadingMore ? "Loading 50 more consignments..." : hasMore ? "Scroll to load more" : visibleTransfers.length ? "All consignments loaded" : null}
      </div>

      {/* Dialogs */}
      <Dialog open={showMultipleLR} onOpenChange={open => {
        if (!generatingMultipleLR) {
          setShowMultipleLR(open);
          if (!open) {
            setSelectedLRIds([]);
            setMultipleLRCopyMode("both");
          }
        }
      }}>
        <DialogContent className="max-w-2xl rounded-none p-0 gap-0">
          <div className="bg-[#1B2A6B] px-6 py-4 border-b-4 border-[#FFD700]">
            <DialogTitle className="text-white font-bold tracking-wide text-base">Print Multiple LR</DialogTitle>
            <p className="text-xs text-blue-100 mt-1">Select consignments from the currently loaded list.</p>
          </div>
          <div className="px-6 py-4 border-b border-[#E2E8F0] flex items-center justify-between">
            <span className="text-sm font-semibold text-[#0F172A]">{selectedLRIds.length} selected</span>
            <button
              type="button"
              onClick={() => setSelectedLRIds(selectedLRIds.length === visibleTransfers.length ? [] : visibleTransfers.map(item => item.id))}
              className="text-xs font-bold uppercase tracking-wider text-[#1B2A6B] hover:underline"
            >
              {selectedLRIds.length === visibleTransfers.length ? "Clear All" : "Select All Loaded"}
            </button>
          </div>
          <fieldset className="border-b border-[#E2E8F0] px-6 py-4">
            <legend className="mb-2 text-[10px] font-bold uppercase tracking-widest text-[#475569]">Copies to Print</legend>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
              {[
                { value: "customer", label: "Customer Copy Only" },
                { value: "driver", label: "Driver Copy Only" },
                { value: "both", label: "Print Both" },
              ].map(option => (
                <label key={option.value} className={`flex cursor-pointer items-center gap-2 border px-3 py-2.5 text-xs font-semibold ${multipleLRCopyMode === option.value ? "border-[#1B2A6B] bg-[#EEF0FA] text-[#1B2A6B]" : "border-[#E2E8F0] text-[#475569] hover:border-[#94A3B8]"}`}>
                  <input
                    type="radio"
                    name="multiple-lr-copy-mode"
                    value={option.value}
                    checked={multipleLRCopyMode === option.value}
                    onChange={() => setMultipleLRCopyMode(option.value)}
                    className="accent-[#1B2A6B]"
                  />
                  {option.label}
                </label>
              ))}
            </div>
          </fieldset>
          <div className="max-h-[55vh] overflow-y-auto divide-y divide-[#E2E8F0]">
            {visibleTransfers.map(transfer => {
              const selected = selectedLRIds.includes(transfer.id);
              return (
                <label key={transfer.id} className="flex items-center gap-3 px-6 py-3 cursor-pointer hover:bg-[#F8FAFC]">
                  <input
                    type="checkbox"
                    checked={selected}
                    onChange={() => setSelectedLRIds(ids => selected ? ids.filter(id => id !== transfer.id) : [...ids, transfer.id])}
                    className="h-4 w-4 accent-[#1B2A6B]"
                  />
                  <span className="font-mono font-bold text-[#1B2A6B] min-w-28">{transfer.lr_number || "No LR"}</span>
                  <span className="text-sm font-semibold text-[#0F172A]">{transfer.customer_name || transfer.consignor_name || "Unnamed customer"}</span>
                </label>
              );
            })}
          </div>
          <DialogFooter className="px-6 py-4 bg-[#F9FAFB] border-t border-[#E2E8F0] gap-2">
            <Button type="button" variant="outline" disabled={generatingMultipleLR} onClick={() => { setShowMultipleLR(false); setSelectedLRIds([]); setMultipleLRCopyMode("both"); }} className="rounded-none">Cancel</Button>
            <Button type="button" disabled={!selectedLRIds.length || generatingMultipleLR} onClick={generateMultipleLR} className="rounded-none bg-[#1B2A6B] hover:bg-[#152257] text-white">
              <Printer size={14} className="mr-1.5" />{generatingMultipleLR ? "Generating..." : `Generate & Print ${selectedLRIds.length || ""}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <CreateTransferDialog
        open={showCreate}
        onClose={() => setShowCreate(false)}
        onCreated={t => { setTransfers(prev => [t, ...prev]); setTotal(value => value + 1); }}
        userRole={user?.role}
      />

      <AssignConsignmentDialog
        open={!!assignTarget}
        transfer={assignTarget}
        consignments={consignments}
        onClose={() => setAssignTarget(null)}
        onSaved={updated => { handleAssigned(updated); setAssignTarget(updated); }}
      />

      <EditTransferDialog
        open={!!editTransfer}
        transfer={editTransfer}
        onClose={() => setEditTransfer(null)}
        onSaved={handleEdited}
      />
    </div>
  );
}
